import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import FieldActionButtons from 'metadata/FieldActionButtons';
import * as TransferUtil from 'samples/transfers/TransferUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {NumericInputChangeEvent} from 'util/Types';

import {MeasurementType, NewMeasurementForm} from './MeasurementForm';

interface Props {
  isDisabled: boolean;
  measurement: LegacyMeasurement;
  onComplete: (measurement: LegacyMeasurement) => void;
  measurementType: MeasurementType;
  // Wait for confirmation from this function before entering edit mode
  confirmBeforeStartEditing?: () => Promise<boolean>;
  disableEditsWithTooltip?: string | null;
  dataTestAttrs?: DataTestAttrs;
}

interface State {
  isBeingEdited: boolean;
  measurement: LegacyMeasurement;
}

export default class ConfirmableMeasurementField extends React.Component<Props, State> {
  static defaultProps = {
    isDisabled: false,
  };

  state: State = {
    isBeingEdited: false,
    measurement: this.props.measurement,
  };

  private updateMeasurement = (fields: Partial<LegacyMeasurement>): void => {
    this.setState({measurement: {...this.state.measurement, ...fields}});
  };

  private handleUnitsChange = ({val: units}: {val: string | null}): void => {
    this.updateMeasurement({units});
  };

  private handleValueChange = (e: NumericInputChangeEvent): void => {
    this.updateMeasurement({value: e.target.numericValue});
  };

  private handleComplete = (): void => {
    this.props.onComplete(this.state.measurement);
    this.setState({isBeingEdited: false});
  };

  private handleEdit = (): void => {
    this.setState({isBeingEdited: true});
  };

  private handleEditWithConfirmation = async (): Promise<void> => {
    assert(this.props.confirmBeforeStartEditing != null, 'Expected confirmBeforeStartEditing to be provided');
    const shouldProceed = await this.props.confirmBeforeStartEditing();
    if (shouldProceed) {
      this.setState({isBeingEdited: true});
    }
  };

  private handleCancel = (): void => {
    this.setState({
      isBeingEdited: false,
      measurement: this.props.measurement,
    });
  };

  private renderForm = (): JSX.Element => {
    const sharedProps = {
      measurement: this.state.measurement,
      onChangeUnits: this.handleUnitsChange,
      onChangeValue: this.handleValueChange,
    };

    return <NewMeasurementForm {...sharedProps} measurementType={this.props.measurementType} />;
  };

  render(): JSX.Element {
    const measurement = this.props.measurement;
    const isDisabled = this.props.isDisabled || this.props.disableEditsWithTooltip != null;
    return (
      <div className={classNames('editableField', {'is-beingEdited': this.state.isBeingEdited})}>
        {this.state.isBeingEdited ? (
          this.renderForm()
        ) : (
          <Tooltip
            text={this.props.disableEditsWithTooltip}
            isDisabled={this.props.disableEditsWithTooltip == null}
          >
            <div
              className={classNames({'u-faded': measurement?.value == null})}
              {...getTestProps(this.props.dataTestAttrs)}
            >
              {TransferUtil.getLegacyMeasurementString(measurement)}
            </div>
          </Tooltip>
        )}
        {!isDisabled && (
          <FieldActionButtons
            cancel={this.handleCancel}
            complete={this.handleComplete}
            edit={
              this.props.confirmBeforeStartEditing != null ? this.handleEditWithConfirmation : this.handleEdit
            }
            isCompact={true}
            isCompleteEnabled={
              (this.state.measurement?.value == null) === (this.state.measurement?.units == null)
            }
            isEditing={this.state.isBeingEdited}
          />
        )}
      </div>
    );
  }
}
